import React, {useCallback, useEffect, useRef, useState} from 'react';
import './styles.scss';
import clearIcon from '../../../icons/clear_icon.svg';
import {TypeaheadSearchProps} from "./props";
import {TypeAheadSuggestions} from "./components/TypeaheadSuggestions";
import {TypeAheadClearButton} from "./components/TypeaheadClearButton";
import {TypeAheadSearchMagnifier} from "./components/TypeAheadSearchMaginfier";

const getSearchValue = (search?: string) => search || '';

function TypeaheadSearch({
  options,
  search,
  placeholder,
  notFoundText,
  onSearchChange,
  onKeyUp,
  focus,
  enterKeyHint,
  role,
  onFocus,
  onBlur,
  onClickInput,
  onKeyDown,
  closeIcon = clearIcon,
  minCharacters = 0,
  hideClearButton = false,
  hideSuggestions = false,
  hideSearchIcon = true,
  inputType = 'text',
}: TypeaheadSearchProps) {
  const [searchedValue, setSearchedValue] = useState(() => {
    return getSearchValue(search);
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showClearButton, setShowClearButton] = useState(() => !!searchedValue?.length);
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const newSearch = getSearchValue(search);
    if (newSearch !== searchedValue) {
      setSearchedValue(newSearch);
    }
  }, [search]);

  useEffect(() => {
    if (!searchedValue) {
      setFilteredOptions([]);
      return;
    }
    const filteredSuggestions = options.filter(option =>
      option.toLowerCase().includes(searchedValue.toLowerCase())
    );
    setFilteredOptions(filteredSuggestions);
  }, [searchedValue, options]);

  useEffect(() => {
    setFilteredOptions(options);
  }, [options]);

  useEffect(() => {
    if (focus) {
      inputRef.current?.focus();
    }
  }, [focus]);

  const handleOnChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const length = value.length;
    setShowClearButton(length > 0);
    if (value === '') {
      handleClear();
      return;
    }
    setSearchedValue(value);
    setShowSuggestions(length >= minCharacters);
  }, [minCharacters]);

  const handleSuggestionClick = (suggestion: string) => {
    setSearchedValue(suggestion);
    setShowSuggestions(!showSuggestions);    
    onSearchChange?.(suggestion, handleClear);
  };

  const doSearch = () => {
    onSearchChange?.(searchedValue, handleClear);
  }

  const handleClear = () => {
    setSearchedValue('');
    setShowSuggestions(false);
    setShowClearButton(false);
  };
  const displayClear = showClearButton && !hideClearButton;
  const displaySearchIcon = showClearButton && !hideSearchIcon;
  const displaySuggestions = showSuggestions && !hideSuggestions;
  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyUp) {
      onKeyUp(e, handleClear);
    }
  }
  return (
    <div className='typeahead-search' aria-expanded={showSuggestions}>
      <div className='typeahead-search__input-container'>
        <label className='typeahead-search__input-label' htmlFor='searchInputTypeAhead'>
          {placeholder}
        </label>
        <input
          ref={inputRef}
          className='typeahead-search__input'
          type={inputType}
          value={searchedValue}
          onChange={handleOnChange}
          placeholder={placeholder}
          id='searchInputTypeAhead'
          aria-controls='typeahead-search-suggestions'
          aria-autocomplete='list'
          aria-labelledby="searchInputTypeAhead"
          name='searchInputTypeAhead'
          onFocus={onFocus}
          onBlur={onBlur}
          onClick={onClickInput}
          onTouchEnd={() => inputRef.current?.focus()}
          onKeyDown={onKeyDown}
          onKeyUp={handleKeyUp}
          {...(enterKeyHint ? { enterKeyHint } : {})}
          {...(role ? { role } : {})}
        />
        <TypeAheadClearButton handleClear={handleClear} closeIcon={closeIcon} display={displayClear} />
        <TypeAheadSearchMagnifier display={displaySearchIcon} doSearch={doSearch} />
      </div>
      <TypeAheadSuggestions
        filteredOptions={filteredOptions}
        search={searchedValue}
        notFoundText={notFoundText}
        handleSuggestionClick={handleSuggestionClick}
        display={displaySuggestions} />
    </div>
  );
}

export default React.memo(TypeaheadSearch);
